import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const CATEGORIES = ["Guide", "Legislation", "Case Study", "News", "Technical", "Industry Insight", "Sector Brief"];
const SECTIONS = [
  { value: "hub", label: "Compliance Hub (/compliance-hub)" },
  { value: "blog", label: "Blog (/blog)" },
];

const emptyForm = { id: null, title: "", category: "Guide", section: "hub", excerpt: "", content: "", cover_image: "", published: true };

export default function AdminBlog() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/blog").then(r => setPosts(r.data)).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) {
        const { id, ...body } = editing;
        await api.patch(`/blog/${id}`, body);
        toast.success("Post updated");
      } else {
        await api.post("/blog", editing);
        toast.success("Post published");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/blog/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const togglePublish = async (p) => {
    try {
      await api.patch(`/blog/${p.id}`, { published: !p.published });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="p-8 lg:p-10" data-testid="page-admin-blog">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Content</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">Articles &amp; Blog</h1>
          <p className="text-slate-500 mt-2">Manage both the Compliance Hub and the Blog from one place.</p>
        </div>
        <button onClick={() => setEditing(emptyForm)} className="gs-gradient text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:opacity-90" data-testid="new-post-btn">
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      {posts === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="bg-white border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-6 py-3 font-medium">Title</th>
                <th className="px-6 py-3 font-medium">Section</th>
                <th className="px-6 py-3 font-medium">Category</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Updated</th>
                <th className="px-6 py-3 font-medium w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400">No posts yet.</td></tr>}
              {posts.map(p => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`post-row-${p.id}`}>
                  <td className="px-6 py-4 font-medium text-slate-900">{p.title}</td>
                  <td className="px-6 py-4 text-slate-600">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${p.section === "blog" ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700"}`}>
                      {p.section === "blog" ? "Blog" : "Hub"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{p.category}</td>
                  <td className="px-6 py-4">
                    <button onClick={() => togglePublish(p)} className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 flex items-center gap-1 ${p.published ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid={`toggle-publish-${p.id}`}>
                      {p.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {p.published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-slate-500">{new Date(p.updated_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-3">
                      <button onClick={() => setEditing({ id: p.id, title: p.title, category: p.category, section: p.section || "hub", excerpt: p.excerpt, content: p.content, cover_image: p.cover_image || "", published: p.published })} className="text-[#1FA59A] text-xs font-semibold hover:underline" data-testid={`edit-post-${p.id}`}>Edit</button>
                      <button onClick={() => remove(p.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-post-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setEditing(null)} data-testid="post-editor">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white w-full max-w-2xl h-full overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex justify-between items-start">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{editing.id ? "Edit" : "New"} post</p>
                <h2 className="font-display text-2xl font-bold">{editing.id ? "Update article" : "New article"}</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-900"><X /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Title</label>
                <input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="post-title" />
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Section</label>
                <select value={editing.section || "hub"} onChange={(e) => setEditing({ ...editing, section: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="post-section">
                  {SECTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <p className="text-xs text-slate-500 mt-1">Hub = legislation/guides at /compliance-hub. Blog = insights/case studies at /blog.</p>
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Category</label>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="post-category">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Cover image URL</label>
                <input value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="post-cover" />
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Excerpt</label>
                <textarea required rows={3} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="post-excerpt" />
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Content (HTML supported)</label>
                <textarea required rows={12} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="w-full border border-slate-300 px-4 py-3 font-mono text-xs focus:outline-none focus:border-[#1FA59A]" data-testid="post-content" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="post-published" />
                <span className="text-sm text-slate-700">Published</span>
              </label>
            </div>
            <div className="p-6 border-t border-slate-200">
              <button type="submit" disabled={saving} className="gs-gradient text-white font-semibold px-6 py-3 w-full hover:opacity-90 disabled:opacity-50" data-testid="post-save">
                {saving ? "Saving…" : editing.id ? "Update post" : "Publish post"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
